import { useState, useCallback } from 'react';
import { Button, Icon } from '@/components/atoms';
import { Input } from '@kisanlink/ui-commons';
import { useBulkUpdatePricesMutation } from '@/hooks/useErpApi';

interface BulkUpdateRow {
  product_id: string;
  variant_id: string;
  price_type: string;
  price: string;
  product_name?: string;
  variant_name?: string;
}

interface BulkPriceUpdateProps {
  onSuccess: () => void;
  onCancel: () => void;
}

const PRICE_TYPES = [
  { value: 'msp', label: 'msp (maximum selling price)' },
  { value: 'member', label: 'member' },
  { value: 'retail', label: 'retail' },
  { value: 'mrp', label: 'mrp (maximum retail price)' },
];

export function BulkPriceUpdate({ onSuccess, onCancel }: BulkPriceUpdateProps) {
  const [updateRows, setUpdateRows] = useState<BulkUpdateRow[]>([
    { product_id: '', variant_id: '', price_type: 'retail', price: '' },
  ]);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [csvFile, setCsvFile] = useState<File | null>(null);

  const { mutate: bulkUpdate, isLoading } = useBulkUpdatePricesMutation();
  // Keep for future use when adding product selection dropdown
  // const { data: products } = useProductsList();

  const addRow = () => {
    setUpdateRows([
      ...updateRows,
      { product_id: '', variant_id: '', price_type: 'retail', price: '' },
    ]);
  };

  const removeRow = (index: number) => {
    setUpdateRows(updateRows.filter((_, i) => i !== index));
  };

  const updateRow = (index: number, field: keyof BulkUpdateRow, value: string) => {
    const newRows = [...updateRows];
    newRows[index] = { ...newRows[index], [field]: value };
    setUpdateRows(newRows);
  };

  const validateRows = (): boolean => {
    for (let i = 0; i < updateRows.length; i++) {
      const row = updateRows[i];

      if (!row.variant_id) {
        setError(`Row ${i + 1}: Variant ID is required`);
        return false;
      }

      if (!row.price || parseFloat(row.price) <= 0) {
        setError(`Row ${i + 1}: Valid price is required`);
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!validateRows()) {
      return;
    }

    try {
      const updates = updateRows.map((row) => ({
        product_id: row.product_id || row.variant_id, // Fallback to variant_id if product_id not provided
        variant_id: row.variant_id,
        price_type: row.price_type,
        price: parseFloat(row.price),
      }));

      const result = await bulkUpdate(updates);

      if (result && 'error_count' in result && result.error_count > 0) {
        setError(
          `Bulk update completed with ${result.error_count} error(s). ${result.success_count} prices updated successfully.`
        );
      } else {
        setSuccess(
          `Successfully updated ${result && 'success_count' in result ? result.success_count : updateRows.length} prices`
        );
        setTimeout(() => {
          onSuccess();
        }, 2000);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update prices');
    }
  };

  const handleCSVUpload = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setCsvFile(file);

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const text = event.target?.result as string;
        const lines = text.split('\n').filter((line) => line.trim());

        if (lines.length < 2) {
          setError('CSV file must have at least one data row');
          return;
        }

        // Skip header row
        const dataLines = lines.slice(1);
        const rows: BulkUpdateRow[] = dataLines.map((line) => {
          const [product_id, variant_id, price_type, price] = line.split(',').map((s) => s.trim());
          return {
            product_id: product_id || '',
            variant_id: variant_id || '',
            price_type: price_type || 'selling_price',
            price: price || '',
          };
        });

        setUpdateRows(rows);
        setSuccess(`Loaded ${rows.length} rows from CSV`);
      } catch (err) {
        setError('Failed to parse CSV file. Please check the format.');
      }
    };

    reader.readAsText(file);
  }, []);

  const downloadTemplate = () => {
    const template =
      'product_id,variant_id,price_type,price\n' +
      'prod_123,var_456,msp,100.00\n' +
      'prod_123,var_456,member,90.00\n' +
      'prod_123,var_456,retail,95.00\n' +
      'prod_123,var_456,mrp,120.00\n';

    const blob = new Blob([template], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'bulk_price_update_template.csv';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Instructions */}
      <div className="bg-info-50 border border-info-200 rounded-lg p-4">
        <div className="flex gap-3">
          <Icon name="Info" size={20} className="text-info-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-info-900">
            <p className="font-medium mb-2">Bulk Price Update Instructions</p>
            <ul className="space-y-1 text-info-700">
              <li>• Enter variant IDs and prices directly, or upload a CSV file</li>
              <li>• CSV format: product_id,variant_id,price_type,price</li>
              <li>• Price types: msp, member, retail, mrp</li>
              <li>• All prices must be positive numbers</li>
              <li>• Download the template for reference</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Error/Success Messages */}
      {error && (
        <div className="bg-error-50 border border-error-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Icon name="CircleAlert" size={20} className="text-error-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-error-700">{error}</p>
          </div>
        </div>
      )}

      {success && (
        <div className="bg-success-50 border border-success-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <Icon name="CircleCheck" size={20} className="text-success-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-success-700">{success}</p>
          </div>
        </div>
      )}

      {/* CSV Upload */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <label className="block text-sm font-medium text-neutral-700">Import from CSV</label>
          <Button type="button" variant="ghost" size="sm" onClick={downloadTemplate}>
            <Icon name="Download" size={16} className="mr-2" />
            Download Template
          </Button>
        </div>
        <div className="flex items-center gap-3">
          <Input type="file" accept=".csv" onChange={handleCSVUpload} className="flex-1" />
          {csvFile && (
            <div className="flex items-center gap-2 text-sm text-neutral-600">
              <Icon name="File" size={16} className="text-success-600" />
              {csvFile.name}
            </div>
          )}
        </div>
      </div>

      <div className="border-t border-neutral-200 pt-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-medium text-neutral-700">Manual Entry</h3>
          <Button type="button" variant="secondary" size="sm" onClick={addRow}>
            <Icon name="Plus" size={16} className="mr-2" />
            Add Row
          </Button>
        </div>

        {/* Bulk Update Table */}
        <div className="border border-neutral-200 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-neutral-50 border-b border-neutral-200">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-neutral-700 uppercase">
                    #
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-neutral-700 uppercase">
                    Variant ID <span className="text-error-600">*</span>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-neutral-700 uppercase">
                    Price Type <span className="text-error-600">*</span>
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-neutral-700 uppercase">
                    Price (INR) <span className="text-error-600">*</span>
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-neutral-700 uppercase">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-200">
                {updateRows.map((row, index) => (
                  <tr key={index} className="hover:bg-neutral-50">
                    <td className="px-4 py-3 text-sm text-neutral-600">{index + 1}</td>
                    <td className="px-4 py-3">
                      <Input
                        type="text"
                        value={row.variant_id}
                        onChange={(e) => updateRow(index, 'variant_id', e.target.value)}
                        placeholder="var_123..."
                        className="w-full"
                        required
                      />
                    </td>
                    <td className="px-4 py-3">
                      <select
                        value={row.price_type}
                        onChange={(e) => updateRow(index, 'price_type', e.target.value)}
                        className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                        required
                      >
                        {PRICE_TYPES.map((type) => (
                          <option key={type.value} value={type.value}>
                            {type.label}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-4 py-3">
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        value={row.price}
                        onChange={(e) => updateRow(index, 'price', e.target.value)}
                        placeholder="0.00"
                        className="w-full"
                        required
                      />
                    </td>
                    <td className="px-4 py-3 text-center">
                      {updateRows.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeRow(index)}
                          className="text-error-600 hover:text-error-900 p-2 rounded-lg hover:bg-error-50"
                        >
                          <Icon name="Trash2" size={16} />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {updateRows.length === 0 && (
          <div className="text-center py-8 text-neutral-500">
            <p>No rows to update. Click &quot;Add Row&quot; or upload a CSV file.</p>
          </div>
        )}
      </div>

      {/* Performance Warning */}
      {updateRows.length > 50 && (
        <div className="bg-warning-50 border border-warning-200 rounded-lg p-4">
          <div className="flex gap-3">
            <Icon
              name="TriangleAlert"
              size={20}
              className="text-warning-600 flex-shrink-0 mt-0.5"
            />
            <div className="text-sm text-warning-900">
              <p className="font-medium">Performance Notice</p>
              <p className="text-warning-700 mt-1">
                You are updating {updateRows.length} prices. This may take a few moments to
                complete.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center justify-between pt-4 border-t border-neutral-200">
        <p className="text-sm text-neutral-600">
          {updateRows.length} {updateRows.length === 1 ? 'price' : 'prices'} to update
        </p>
        <div className="flex items-center gap-3">
          <Button type="button" variant="secondary" onClick={onCancel} disabled={isLoading}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" disabled={isLoading || updateRows.length === 0}>
            {isLoading ? (
              <>
                <Icon name="Loader" size={16} className="mr-2 animate-spin" />
                Updating...
              </>
            ) : (
              <>
                <Icon name="Upload" size={16} className="mr-2" />
                Update {updateRows.length} {updateRows.length === 1 ? 'Price' : 'Prices'}
              </>
            )}
          </Button>
        </div>
      </div>
    </form>
  );
}
